import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Chip, TextField } from '@nyce/admin-kit/ui';
import { EmptyState, ErrorBox, Loading, Page, TableScroller, useAsync } from '../../components/common';
import { TargetShell } from '../../identity/TargetShell';
import { activeTarget, activeTargetInfo, scimFor } from '../../identity/session';
import type { Customer, Tenant } from '../../identity/api';
import { loadAuthDirectory } from '../../pages/statistics/links';
import { setupFor } from '../../buddy/api/setup';
import { automationGw } from '../../automation/api/client';
import { SCOPES, type Environment } from '../../api';
import { api } from '../../session';
import { useOnboardingConfig } from '../api/config';
import { useScope } from '../wizard/parts';
import '../onboarding.css';

type Cell = { done: boolean; detail?: string } | null;

/** One customer's onboarding state, derived from the services (null = not applicable / not readable here). */
export interface CustomerRow {
  customer: Customer;
  tenants: Tenant[];
  cells: Record<string, Cell>;
}

export interface ChecklistData {
  customers: Customer[];
  tenants: Tenant[];
  environments: Environment[] | null;
  buddyOrgs: Set<string> | null;
  gatewayTenants: Set<string> | null;
  scimTenants: Set<string> | null;
}

const lower = (s: string | null | undefined) => (s ?? '').toLowerCase();
const has = (t: Tenant, system: string) => (t.aliases ?? []).some((a) => a.system === system);

/** Pure: the checklist rows (tested). */
export function checklistRows(d: ChecklistData): CustomerRow[] {
  return d.customers.map((customer) => {
    const tenants = d.tenants.filter((t) => t.customerId === customer.id && t.status !== 'Deleted');
    const ids = new Set(tenants.map((t) => lower(t.id)));
    const envs = (d.environments ?? []).filter((e) => e.authTenantId && ids.has(lower(e.authTenantId)));
    const withClient = tenants.filter((t) =>
      (t.claims ?? []).some((c) => c.value === 'datacollector:write' || c.value === 'boxcalc:calculate')
    );
    return {
      customer,
      tenants,
      cells: {
        tenant: { done: tenants.length > 0, detail: tenants.map((t) => t.name).join(', ') || undefined },
        installation:
          d.environments === null
            ? null
            : { done: envs.length > 0, detail: envs.map((e) => e.name).join(', ') || undefined },
        services: { done: withClient.length > 0 },
        statistics: {
          done:
            (customer.aliases ?? []).some((a) => a.system === 'pms') || tenants.some((t) => has(t, 'pms')),
          detail:
            (customer.aliases ?? [])
              .filter((a) => a.system === 'pms')
              .map((a) => a.value)
              .join(', ') || undefined,
        },
        buddy: d.buddyOrgs === null ? null : { done: d.buddyOrgs.has(lower(customer.id)) },
        printqueue: {
          // linked by alias (organization name), or AUTH only: the tenant holds both print queue scopes
          done: tenants.some(
            (t) =>
              has(t, 'printqueue') ||
              ['printqueue:enqueue', 'printqueue:print'].every((v) =>
                (t.claims ?? []).some((c) => c.value === v)
              )
          ),
          detail:
            tenants
              .flatMap((t) => (t.aliases ?? []).filter((a) => a.system === 'printqueue').map((a) => a.value))
              .join(', ') || undefined,
        },
        automation:
          d.gatewayTenants === null
            ? null
            : { done: tenants.some((t) => d.gatewayTenants!.has(lower(t.id))) },
        scim: d.scimTenants === null ? null : { done: tenants.some((t) => d.scimTenants!.has(lower(t.id))) },
      },
    };
  });
}

const COLUMNS: { key: string; label: string; wizard: string }[] = [
  { key: 'tenant', label: 'Tenant', wizard: '/onboarding/tenant' },
  { key: 'services', label: 'Service scopes', wizard: '/onboarding/tenant' },
  { key: 'installation', label: 'Hub environment', wizard: '/onboarding/installation' },
  { key: 'statistics', label: 'Statistics', wizard: '/onboarding/statistics' },
  { key: 'buddy', label: 'nyceBuddy', wizard: '/onboarding/buddy' },
  { key: 'printqueue', label: 'Print queue', wizard: '/onboarding/printqueue' },
  { key: 'automation', label: 'Automation', wizard: '/onboarding/automation' },
  { key: 'scim', label: 'SCIM', wizard: '/onboarding/scim' },
];

/** W10: per customer of an AUTH environment, which modules are set up — each row links to the wizard for the rest. */
export function CustomerChecklistPage() {
  return (
    <Page
      title="Customer checklist"
      description="Every customer of the environment and what is set up for it, read live from AUTH, the registry and the services. A missing module links to its wizard."
    >
      <TargetShell>
        <Checklist />
      </TargetShell>
    </Page>
  );
}

function Checklist() {
  const target = activeTarget();
  const info = activeTargetInfo();
  const config = useOnboardingConfig();
  const canBuddy = useScope(SCOPES.buddyRead);
  const canGw = useScope(SCOPES.automationRead);
  const canScim = useScope(SCOPES.scimRead);
  const [q, setQ] = useState('');
  const isHub = !!config && config.hubTarget === target;
  const data = useAsync(async (): Promise<ChecklistData> => {
    const { customers, tenants } = await loadAuthDirectory(target);
    const [environments, buddyOrgs, gatewayTenants, scimTenants] = await Promise.all([
      isHub ? api.environments().catch(() => null) : Promise.resolve(null),
      info?.buddyUrl && canBuddy
        ? setupFor(target)
            .organizations.list()
            .then((l) => new Set(l.map((o) => lower(o.id))))
            .catch(() => null)
        : Promise.resolve(null),
      info?.automationGw && canGw
        ? automationGw(target)
            .tenants()
            .then((l) => new Set(l.filter((t) => t.authTenantId).map((t) => lower(t.authTenantId))))
            .catch(() => null)
        : Promise.resolve(null),
      info?.scimUrl && canScim
        ? scimFor(target)
            .list()
            .then((l) => new Set(l.filter((e) => e.target === 'auth').map((e) => lower(e.tenantId))))
            .catch(() => null)
        : Promise.resolve(null),
    ]);
    return { customers, tenants, environments, buddyOrgs, gatewayTenants, scimTenants };
  }, [target, isHub]);
  const rows = useMemo(() => {
    const all = data.data ? checklistRows(data.data) : [];
    const s = q.trim().toLowerCase();
    return all
      .filter(
        (r) =>
          !s ||
          r.customer.name.toLowerCase().includes(s) ||
          r.tenants.some((t) => t.name.toLowerCase().includes(s))
      )
      .sort((a, b) => a.customer.name.localeCompare(b.customer.name));
  }, [data.data, q]);

  const cell = (r: CustomerRow, c: (typeof COLUMNS)[number]): ReactNode => {
    const v = r.cells[c.key];
    if (v === null) return <span className="nyk-ui-hint">—</span>;
    if (v.done)
      return (
        <Chip size="sm" className="oph-status nyk-tone-green" title={v.detail}>
          ✓{v.detail ? ` ${v.detail}` : ''}
        </Chip>
      );
    const params = new URLSearchParams({ target, customer: r.customer.id });
    // each wizard names the AUTH tenant its own way (`tenant` is the print queue's TenantId there)
    const tenantKey = c.key === 'printqueue' || c.key === 'automation' ? 'authTenant' : 'tenant';
    if (r.tenants[0] && c.key !== 'installation' && c.key !== 'statistics')
      params.set(tenantKey, r.tenants[0].id);
    return (
      <Link className="nyk-link" to={`${c.wizard}?${params}`}>
        Set up
      </Link>
    );
  };

  return (
    <>
      <div className="ob-toolbar ob-noprint">
        <TextField
          aria-label="Search"
          placeholder="Customer or tenant"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      <ErrorBox error={data.error} />
      <Loading loading={data.loading && !data.data} />
      {data.data && !rows.length && <EmptyState card title="No customers." />}
      {!!rows.length && (
        <div className="nyk-card">
          <TableScroller>
            <table className="nyk-ui-table nyk-cards ob-matrix" data-testid="checklist">
              <thead>
                <tr>
                  <th>Customer</th>
                  {COLUMNS.map((c) => (
                    <th key={c.key}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.customer.id} data-customer={r.customer.name}>
                    <td className="nyk-card-title">{r.customer.name}</td>
                    {COLUMNS.map((c) => (
                      <td key={c.key} data-label={c.label} data-cell={c.key}>
                        {cell(r, c)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroller>
        </div>
      )}
    </>
  );
}
